import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Plus, TrendingUp, User, Calendar, X, ArrowRight, StickyNote, IndianRupee, Search, Trash2 } from 'lucide-react'
import { getDeals, createDeal as createDealSvc, updateDeal as updateDealSvc, deleteDeal as deleteDealSvc } from '../services/deals.service'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import type { Deal } from '../types'

// ── Add Deal Modal ─────────────────────────────────────────────
function AddDealModal({ onClose, onAdd }: { onClose: () => void; onAdd: (d: Omit<Deal, 'id' | 'createdAt'>) => void }) {
  const today    = new Date().toISOString().split('T')[0]
  const close30  = new Date(Date.now() + 30 * 864e5).toISOString().split('T')[0]
  const [form, setForm] = useState({
    title: '', company: '', contactName: '', value: '',
    stage: 'lead' as Deal['stage'], probability: '20',
    expectedClose: close30, notes: '',
  })
  const [error, setError] = useState('')

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim())   { setError('Deal title is required.'); return }
    if (!form.company.trim()) { setError('Company name is required.'); return }
    const val = parseFloat(form.value.replace(/[^0-9.]/g, ''))
    if (!val || val <= 0) { setError('Enter a valid deal value.'); return }
    onAdd({
      title:         form.title.trim(),
      company:       form.company.trim(),
      contactName:   form.contactName.trim() || '—',
      value:         val,
      stage:         form.stage,
      probability:   parseInt(form.probability) || 20,
      expectedClose: form.expectedClose,
      notes:         form.notes.trim(),
    })
    onClose()
  }

  const inputCls   = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }
  const focusOn    = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')
  const focusOff   = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>New Deal</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DEAL TITLE *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. Full-funnel Paid Ads" className={inputCls} style={inputStyle} autoFocus
              onFocus={focusOn} onBlur={focusOff} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>COMPANY *</label>
              <input value={form.company} onChange={e => set('company', e.target.value)}
                placeholder="Company name" className={inputCls} style={inputStyle}
                onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>CONTACT</label>
              <input value={form.contactName} onChange={e => set('contactName', e.target.value)}
                placeholder="Contact name" className={inputCls} style={inputStyle}
                onFocus={focusOn} onBlur={focusOff} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>DEAL VALUE (₹) *</label>
              <input value={form.value} onChange={e => set('value', e.target.value)}
                placeholder="e.g. 150000" className={inputCls} style={inputStyle}
                onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>STAGE</label>
              <select value={form.stage} onChange={e => set('stage', e.target.value)} className={inputCls} style={inputStyle}>
                <option value="lead">Lead</option>
                <option value="qualified">Qualified</option>
                <option value="proposal">Proposal</option>
                <option value="negotiation">Negotiation</option>
                <option value="closed-won">Closed Won</option>
                <option value="closed-lost">Closed Lost</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>PROBABILITY (%)</label>
              <input value={form.probability} onChange={e => set('probability', e.target.value)}
                placeholder="20" className={inputCls} style={inputStyle}
                onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>EXPECTED CLOSE</label>
              <input type="date" value={form.expectedClose} onChange={e => set('expectedClose', e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>NOTES</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Discovery call notes, context…" rows={2}
              className={inputCls} style={{ ...inputStyle, resize: 'none' }}
              onFocus={focusOn} onBlur={focusOff} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              Add Deal
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Deal Detail Modal ──────────────────────────────────────────
const ALL_STAGES: { key: Deal['stage']; label: string }[] = [
  { key: 'lead',         label: 'Lead' },
  { key: 'qualified',    label: 'Qualified' },
  { key: 'proposal',     label: 'Proposal' },
  { key: 'negotiation',  label: 'Negotiation' },
  { key: 'closed-won',   label: 'Closed Won' },
  { key: 'closed-lost',  label: 'Closed Lost' },
]

function DealDetailModal({
  deal, onClose, onStageChange, onDelete, isAdmin,
}: {
  deal: Deal
  onClose: () => void
  onStageChange: (id: string, stage: Deal['stage']) => void
  onDelete: (id: string) => void
  isAdmin: boolean
}) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closeDate = new Date(deal.expectedClose).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  const created   = new Date(deal.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  const probColor = deal.probability >= 70 ? '#e8a000' : deal.probability >= 40 ? '#ff8c00' : '#a0a0a0'

  const wonOrLost = deal.stage === 'closed-won' || deal.stage === 'closed-lost'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm rounded-2xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(255,198,0,0.1)' }}>
          <h2 className="text-sm font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Deal Details</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={16} /></button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* Title + company */}
          <div>
            <p className="text-lg font-bold font-syne text-white leading-snug">{deal.title}</p>
            <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{deal.company}</p>
          </div>

          {/* Value + probability */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,198,0,0.08)' }}>
              <IndianRupee size={13} style={{ color: '#ffc600' }} />
              <span className="text-base font-bold font-syne" style={{ color: '#ffc600' }}>
                {deal.value.toLocaleString('en-IN')}
              </span>
            </div>
            <div className="px-3 py-2 rounded-lg text-sm font-semibold" style={{ background: 'rgba(255,198,0,0.08)', color: probColor }}>
              {deal.probability}% chance
            </div>
          </div>

          {/* Meta rows */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: '#555' }}>Contact</span>
              <span className="flex items-center gap-1.5" style={{ color: '#a0a0a0' }}>
                <User size={12} />{deal.contactName}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: '#555' }}>Expected Close</span>
              <span className="flex items-center gap-1.5" style={{ color: '#a0a0a0' }}>
                <Calendar size={12} />{closeDate}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: '#555' }}>Created</span>
              <span style={{ color: '#a0a0a0' }}>{created}</span>
            </div>
          </div>

          {/* Notes */}
          {deal.notes && (
            <div className="rounded-lg px-3 py-2.5" style={{ background: '#1a1a1a' }}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <StickyNote size={12} style={{ color: '#555' }} />
                <span className="text-xs font-semibold" style={{ color: '#555' }}>NOTES</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#a0a0a0' }}>{deal.notes}</p>
            </div>
          )}

          {/* Move stage */}
          {!wonOrLost && (
            <div>
              <p className="text-xs font-semibold mb-2" style={{ color: '#555' }}>MOVE TO STAGE</p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_STAGES.filter(s => s.key !== deal.stage).map(({ key, label }) => {
                  const isWin  = key === 'closed-won'
                  const isLoss = key === 'closed-lost'
                  return (
                    <button key={key}
                      onClick={() => { onStageChange(deal.id, key); onClose() }}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg transition-all font-medium"
                      style={isWin
                        ? { background: 'rgba(232,160,0,0.12)', color: '#e8a000', border: '1px solid rgba(232,160,0,0.25)' }
                        : isLoss
                        ? { background: 'rgba(204,51,0,0.1)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.25)' }
                        : { background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                      <ArrowRight size={10} />{label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {wonOrLost && (
            <div className="rounded-lg px-3 py-2 text-xs text-center font-medium"
              style={deal.stage === 'closed-won'
                ? { background: 'rgba(232,160,0,0.1)', color: '#e8a000' }
                : { background: 'rgba(204,51,0,0.1)', color: '#cc3300' }}>
              {deal.stage === 'closed-won' ? '🎉 Deal won!' : '❌ Deal lost'}
            </div>
          )}
        </div>

        <div className="px-5 pb-5">
          {confirmDelete ? (
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                Cancel
              </button>
              {isAdmin && (
                <button onClick={() => { onDelete(deal.id); onClose() }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: 'rgba(204,51,0,0.15)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>
                  Yes, Delete
                </button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              {isAdmin && (
                <button onClick={() => setConfirmDelete(true)}
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold flex-shrink-0"
                  style={{ background: '#1a1a1a', color: '#cc3300', border: '1px solid rgba(204,51,0,0.2)' }}>
                  <Trash2 size={14} />
                </button>
              )}
              <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Kanban stages (no closed-lost) ────────────────────────────
const STAGES: { key: Deal['stage']; label: string }[] = [
  { key: 'lead',        label: 'Lead' },
  { key: 'qualified',   label: 'Qualified' },
  { key: 'proposal',    label: 'Proposal' },
  { key: 'negotiation', label: 'Negotiation' },
  { key: 'closed-won',  label: 'Closed Won' },
]

function formatINR(n: number) {
  return '₹' + n.toLocaleString('en-IN')
}

function ProbabilityBadge({ p }: { p: number }) {
  const color = p >= 70 ? '#e8a000' : p >= 40 ? '#ff8c00' : '#a0a0a0'
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.1)', color }}>
      {p}%
    </span>
  )
}

function DealCard({ deal, onClick }: { deal: Deal; onClick: () => void }) {
  return (
    <div
      className="rounded-xl p-3 mb-2 cursor-pointer transition-colors"
      style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.12)' }}
      onClick={onClick}
      onMouseEnter={e => (e.currentTarget.style.background = '#242424')}
      onMouseLeave={e => (e.currentTarget.style.background = '#1a1a1a')}
    >
      <div className="flex items-start justify-between mb-1">
        <p className="text-sm font-semibold text-white leading-tight">{deal.company}</p>
        <ProbabilityBadge p={deal.probability} />
      </div>
      <p className="text-xs mb-2" style={{ color: '#a0a0a0' }}>{deal.title}</p>
      <p className="text-base font-bold font-syne mb-2" style={{ color: '#ffc600' }}>{formatINR(deal.value)}</p>
      <div className="flex items-center gap-3 text-xs" style={{ color: '#a0a0a0' }}>
        <span className="flex items-center gap-1"><User size={11} />{deal.contactName}</span>
        <span className="flex items-center gap-1"><Calendar size={11} />{new Date(deal.expectedClose).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
      </div>
    </div>
  )
}

const STAGE_PROBABILITY: Record<Deal['stage'], number> = {
  lead:          20,
  qualified:     40,
  proposal:      60,
  negotiation:   80,
  'closed-won':  100,
  'closed-lost': 0,
}

export default function Pipeline() {
  const [deals, setDeals]       = useState<Deal[]>([])
  const [showAdd, setShowAdd]   = useState(false)
  const [selected, setSelected] = useState<Deal | null>(null)
  const [search, setSearch]     = useState('')
  const { toast }               = useToast()
  const { addActivity }         = useActivity()
  const isAdmin                 = useIsAdmin()
  const location                = useLocation()

  useEffect(() => {
    getDeals().then(setDeals).catch(console.error)
    if ((location.state as any)?.openAdd) {
      setShowAdd(true)
      window.history.replaceState({}, '')
    }
  }, [])

  // Pipeline shows ALL deals — no date range filter (you need to see your full funnel at all times)
  const matchesSearch = (d: Deal) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return d.title.toLowerCase().includes(q) || d.company.toLowerCase().includes(q) || d.contactName.toLowerCase().includes(q)
  }
  const stageDeals  = (stage: Deal['stage']) => deals.filter(d => d.stage === stage && matchesSearch(d))
  const activeDeals = deals.filter(d => d.stage !== 'closed-lost')
  const lostDeals   = deals.filter(d => d.stage === 'closed-lost' && matchesSearch(d))
  const pipelineValue = activeDeals.reduce((s, d) => s + d.value, 0)
  const wonValue      = deals.filter(d => d.stage === 'closed-won').reduce((s, d) => s + d.value, 0)

  const handleStageChange = async (id: string, stage: Deal['stage']) => {
    const deal = deals.find(d => d.id === id)
    const probability = STAGE_PROBABILITY[stage]
    try { await updateDealSvc(id, { stage, probability }) } catch { toast('Update failed', 'error'); return }
    setDeals(prev => prev.map(d => d.id === id ? { ...d, stage, probability } : d))
    setSelected(prev => prev?.id === id ? { ...prev, stage, probability } : prev)
    if (deal) {
      const label = ALL_STAGES.find(s => s.key === stage)?.label ?? stage
      toast(
        stage === 'closed-won'  ? `🎉 "${deal.title}" closed won!` :
        stage === 'closed-lost' ? `"${deal.title}" marked as lost` :
        `"${deal.title}" moved to ${label}`,
        stage === 'closed-won' ? 'success' : stage === 'closed-lost' ? 'error' : 'info'
      )
      if (stage === 'closed-won') {
        addActivity({ type: 'closed_won', emoji: '🏆', description: `Deal closed won: ${deal.title}`, detail: `${deal.company} · ₹${deal.value.toLocaleString('en-IN')}` })
      } else if (stage === 'closed-lost') {
        addActivity({ type: 'closed_lost', emoji: '❌', description: `Deal marked lost: ${deal.title}`, detail: deal.company })
      }
    }
  }

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-syne text-white">Sales Pipeline</h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>
            Pipeline value: <span style={{ color: '#ffc600' }}>{formatINR(pipelineValue)}</span>
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: '#ffc600', color: '#000' }}>
            <Plus size={15} /> Add Deal
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#555' }} />
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search deals, companies…"
          className="w-full pl-9 pr-3 py-2 rounded-lg text-sm text-white outline-none"
          style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.15)', color: '#fff' }}
          onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.35)')}
          onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.15)')}
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
            style={{ color: '#555' }}>✕</button>
        )}
      </div>

      {/* Summary pills */}
      <div className="flex gap-3 mb-6 overflow-x-auto pb-1">
        {STAGES.map(({ key, label }) => {
          const s = stageDeals(key)
          const total = s.reduce((a, d) => a + d.value, 0)
          return (
            <div key={key} className="flex-shrink-0 px-4 py-2 rounded-lg text-sm" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
              <span className="font-semibold text-white">{label}</span>
              <span className="mx-2" style={{ color: '#555' }}>·</span>
              <span style={{ color: '#a0a0a0' }}>{s.length} deals</span>
              {total > 0 && <span className="ml-1" style={{ color: '#ffc600' }}>· {formatINR(total)}</span>}
            </div>
          )
        })}
        <div className="flex-shrink-0 px-4 py-2 rounded-lg text-sm" style={{ background: 'rgba(232,160,0,0.1)', border: '1px solid rgba(232,160,0,0.25)' }}>
          <TrendingUp size={13} className="inline mr-1" style={{ color: '#e8a000' }} />
          <span style={{ color: '#e8a000' }}>Won: {formatINR(wonValue)}</span>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(5, minmax(200px, 1fr))' }}>
        {STAGES.map(({ key, label }) => {
          const cols = stageDeals(key)
          return (
            <div key={key} className="rounded-xl p-3" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)', minHeight: 400 }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold" style={{ color: key === 'closed-won' ? '#e8a000' : '#fff' }}>{label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(255,198,0,0.12)', color: '#ffc600' }}>{cols.length}</span>
              </div>
              {cols.length === 0 ? (
                <div className="text-center py-8 text-xs" style={{ color: '#555' }}>No deals</div>
              ) : (
                cols.map(d => <DealCard key={d.id} deal={d} onClick={() => setSelected(d)} />)
              )}
            </div>
          )
        })}
      </div>

      {/* Closed Lost */}
      {lostDeals.length > 0 && (
        <details className="mt-6">
          <summary className="cursor-pointer text-sm font-medium mb-3" style={{ color: '#cc3300' }}>
            Closed Lost ({lostDeals.length})
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            {lostDeals.map(d => (
              <div key={d.id} className="rounded-xl p-3 opacity-60 cursor-pointer transition-opacity hover:opacity-80"
                style={{ background: '#0f0f0f', border: '1px solid rgba(204,51,0,0.2)' }}
                onClick={() => setSelected(d)}>
                <p className="text-sm font-semibold text-white">{d.company}</p>
                <p className="text-xs" style={{ color: '#a0a0a0' }}>{d.title} · {formatINR(d.value)}</p>
              </div>
            ))}
          </div>
        </details>
      )}

      {showAdd && (
        <AddDealModal
          onClose={() => setShowAdd(false)}
          onAdd={async d => {
            try {
              const saved = await createDealSvc(d)
              setDeals(prev => [saved, ...prev])
              toast(`Deal "${saved.title}" added`)
              addActivity({ type: 'new_deal', emoji: '🤝', description: `New deal added: ${saved.title}`, detail: `${saved.company} · ₹${saved.value.toLocaleString('en-IN')}` })
            } catch { toast('Failed to add deal', 'error') }
          }}
        />
      )}

      {selected && (
        <DealDetailModal
          deal={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onStageChange={handleStageChange}
          onDelete={async id => {
            const deal = deals.find(d => d.id === id)
            try {
              await deleteDealSvc(id)
              setDeals(prev => prev.filter(d => d.id !== id))
              setSelected(null)
              if (deal) {
                toast(`"${deal.title}" deleted`, 'info')
                addActivity({ type: 'deal_deleted', emoji: '🗑️', description: `Deal deleted: ${deal.title}`, detail: deal.company })
              }
            } catch { toast('Delete failed', 'error') }
          }}
        />
      )}
    </div>
  )
}
